import React, { useState, useEffect, useCallback } from 'react';
import styles from './DockUndockManager.module.css';

const DockUndockManager = ({ 
  reportId,
  reportTitle, 
  tier, 
  onDockStateChange,
  className = '' 
}) => {
  const [isDocked, setIsDocked] = useState(true);
  const [undockedWindow, setUndockedWindow] = useState(null);
  const [windowDimensions, setWindowDimensions] = useState({
    width: 1200,
    height: 800
  });

  // Load dock state from localStorage on component mount
  useEffect(() => {
    const savedDockState = localStorage.getItem(`dock-state-${tier}`);
    if (savedDockState) {
      const { isDocked: savedIsDocked, dimensions } = JSON.parse(savedDockState);
      setIsDocked(savedIsDocked);
      if (dimensions) {
        setWindowDimensions(dimensions);
      }
    }
  }, [tier]);

  // Save dock state to localStorage whenever it changes
  useEffect(() => {
    const dockState = {
      isDocked,
      dimensions: windowDimensions,
      timestamp: Date.now()
    };
    localStorage.setItem(`dock-state-${tier}`, JSON.stringify(dockState));
    
    if (onDockStateChange) {
      onDockStateChange(isDocked);
    }
  }, [isDocked, windowDimensions, tier, onDockStateChange]);

  // Handle window close events
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (undockedWindow && !undockedWindow.closed) {
        undockedWindow.close();
      }
    };

    const handleUnload = () => {
      if (undockedWindow && !undockedWindow.closed) {
        undockedWindow.close();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [undockedWindow]);

  // Check if undocked window is still open
  useEffect(() => {
    if (undockedWindow && !isDocked) {
      const checkWindow = setInterval(() => {
        if (undockedWindow.closed) {
          setIsDocked(true);
          setUndockedWindow(null);
          clearInterval(checkWindow);
        }
      }, 1000);

      return () => clearInterval(checkWindow);
    }
  }, [undockedWindow, isDocked]);

  const handleDockToggle = useCallback(() => {
    if (isDocked) {
      // Undock into a window that fills the available screen.
      const width = window.screen?.availWidth || window.innerWidth;
      const height = window.screen?.availHeight || window.innerHeight;
      const left = window.screen?.availLeft || 0;
      const top = window.screen?.availTop || 0;
      const windowFeatures = [
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'resizable=yes',
        'scrollbars=yes',
        'status=no',
        'toolbar=no',
        'menubar=no',
        'location=no'
      ].join(',');

      if (!reportId) return;

      const params = new URLSearchParams({ reportId });
      const viewUrl = `${window.location.origin}/report-view?${params.toString()}`;
      const newWindow = window.open(
        viewUrl,
        `powerbi-${tier}-${Date.now()}`,
        windowFeatures
      );

      if (newWindow) {
        try {
          newWindow.moveTo(left, top);
          newWindow.resizeTo(width, height);
        } catch (_) {
          // Some browsers block move/resize after open.
        }

        newWindow.addEventListener('load', () => {
          newWindow.document.title = `${reportTitle} - ${tier} Report`;
        });

        const handleResize = () => {
          setWindowDimensions({
            width: newWindow.innerWidth,
            height: newWindow.innerHeight
          });
        };

        newWindow.addEventListener('resize', handleResize);
        newWindow.addEventListener('beforeunload', () => {
          setIsDocked(true);
          setUndockedWindow(null);
        });

        setUndockedWindow(newWindow);
        setIsDocked(false);
      } else {
        console.error('Failed to open new window. Popup blocked?');
      }
    } else {
      // Dock - close undocked window and return to main window
      if (undockedWindow && !undockedWindow.closed) {
        undockedWindow.close();
      }
      setIsDocked(true);
      setUndockedWindow(null);
    }
  }, [isDocked, reportId, reportTitle, tier, undockedWindow]);

  const handleDockAll = useCallback(() => {
    // This will be handled by the parent component
    if (undockedWindow && !undockedWindow.closed) {
      undockedWindow.close();
    }
    setIsDocked(true);
    setUndockedWindow(null);
  }, [undockedWindow]);

  return (
    <div className={`${styles.dockUndockManager} ${className}`}>
      <button
        className={`${styles.dockToggle} ${!isDocked ? styles.undocked : ''}`}
        onClick={handleDockToggle}
        title={isDocked ? 'Undock report' : 'Dock back all reports'}
        aria-label={isDocked ? 'Undock report' : 'Dock report'}
      >
        {isDocked ? '⧉' : '⧈'}
      </button>
    </div>
  );
};

export default DockUndockManager;
